import { InfoIcon } from '@sorrem-client/ui/components';
import { useGameSelector } from '@sorrem-client/ui/hooks';
import type { GameStateBridge } from '@sorrem-client/ui/shared/game-state-bridge';
import type { UIGameState } from '@sorrem-client/types';
import styles from './EngineTelemetryPanel.module.scss';

interface EngineTelemetryPanelProps {
    bridge: GameStateBridge;
}

const getTelemetry = (state: UIGameState): UIGameState['telemetry'] => state.telemetry;

const METRIC_DESCRIPTIONS = {
    fps: 'Frames rendered per second',
    ups: 'Game state updates per second',
    rps: 'Canvas redraws per second',
    busy: 'Percentage of time spent on update and rendering',
    frame: 'Average time to process one complete frame',
    update: 'Average time spent in game state update logic',
    render: 'Average time spent drawing to canvas',
    catchUp: 'Frames that had multiple updates (indicates lag)',
};

export function EngineTelemetryPanel({ bridge }: EngineTelemetryPanelProps): JSX.Element {
    const telemetry = useGameSelector(bridge, getTelemetry);

    return (
        <section className={styles.panel} aria-label="Engine telemetry">
            <div className={styles.header}>
                <h3 className={styles.title}>Engine</h3>
                <span className={styles.meta}>up {telemetry.uptimeSec.toFixed(1)}s</span>
            </div>

            <div className={styles.grid}>
                <div className={styles.metric}>
                    <div className={styles.labelWithIcon}>
                        <span className={styles.label}>FPS</span>
                        <InfoIcon ariaLabel={METRIC_DESCRIPTIONS.fps} popoverContent={METRIC_DESCRIPTIONS.fps} />
                    </div>
                    <span className={styles.value}>{telemetry.fps.toFixed(1)}</span>
                </div>
                <div className={styles.metric}>
                    <div className={styles.labelWithIcon}>
                        <span className={styles.label}>UPS</span>
                        <InfoIcon ariaLabel={METRIC_DESCRIPTIONS.ups} popoverContent={METRIC_DESCRIPTIONS.ups} />
                    </div>
                    <span className={styles.value}>{telemetry.updatesPerSecond.toFixed(1)}</span>
                </div>
                <div className={styles.metric}>
                    <div className={styles.labelWithIcon}>
                        <span className={styles.label}>RPS</span>
                        <InfoIcon ariaLabel={METRIC_DESCRIPTIONS.rps} popoverContent={METRIC_DESCRIPTIONS.rps} />
                    </div>
                    <span className={styles.value}>{telemetry.rendersPerSecond.toFixed(1)}</span>
                </div>
                <div className={styles.metric}>
                    <div className={styles.labelWithIcon}>
                        <span className={styles.label}>Busy</span>
                        <InfoIcon ariaLabel={METRIC_DESCRIPTIONS.busy} popoverContent={METRIC_DESCRIPTIONS.busy} />
                    </div>
                    <span className={styles.value}>{telemetry.busyPercent.toFixed(1)}%</span>
                </div>
            </div>

            <div className={styles.rows}>
                <div className={styles.row}>
                    <div className={styles.rowLabelWithIcon}>
                        <span>Frame</span>
                        <InfoIcon ariaLabel={METRIC_DESCRIPTIONS.frame} popoverContent={METRIC_DESCRIPTIONS.frame} />
                    </div>
                    <span>{telemetry.averageFrameTimeMs.toFixed(1)}ms</span>
                </div>
                <div className={styles.row}>
                    <div className={styles.rowLabelWithIcon}>
                        <span>Update</span>
                        <InfoIcon ariaLabel={METRIC_DESCRIPTIONS.update} popoverContent={METRIC_DESCRIPTIONS.update} />
                    </div>
                    <span>{telemetry.averageUpdateTimeMs.toFixed(2)}ms</span>
                </div>
                <div className={styles.row}>
                    <div className={styles.rowLabelWithIcon}>
                        <span>Render</span>
                        <InfoIcon ariaLabel={METRIC_DESCRIPTIONS.render} popoverContent={METRIC_DESCRIPTIONS.render} />
                    </div>
                    <span>{telemetry.averageRenderTimeMs.toFixed(2)}ms</span>
                </div>
                <div className={styles.row}>
                    <div className={styles.rowLabelWithIcon}>
                        <span>Catch-up</span>
                        <InfoIcon ariaLabel={METRIC_DESCRIPTIONS.catchUp} popoverContent={METRIC_DESCRIPTIONS.catchUp} />
                    </div>
                    <span>{telemetry.catchUpFrameCount}</span>
                </div>
            </div>
        </section>
    );
}